import { Component } from '@theme/component';

/** Storage key, shared with the inline restore script in layout/theme.liquid. */
const STORAGE_KEY = 'theme:tax-display';
const VIEWS = ['net', 'gross'];
const CHANGE_EVENT = 'theme:tax-display:change';

/**
 * Switches between net and gross price views. Display only: snippets/tax-price.liquid renders
 * every price in both variants and the root `data-tax-display` attribute decides which one shows.
 * Cart and checkout calculations are not touched.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} options - One button per view, each with `data-value="net|gross"`.
 *
 * @extends {Component<Refs>}
 */
class TaxDisplayComponent extends Component {
  requiredRefs = ['options'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CHANGE_EVENT, this.#sync);
    this.#sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CHANGE_EVENT, this.#sync);
  }

  /**
   * Handles a click on one of the view buttons.
   * @param {MouseEvent} event - The click event, targeted at a button with `data-value`.
   */
  select(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;

    const view = button.dataset.value;
    if (!view || !VIEWS.includes(view) || view === document.documentElement.dataset.taxDisplay) return;

    document.documentElement.dataset.taxDisplay = view;

    try {
      localStorage.setItem(STORAGE_KEY, view);
    } catch (_) {
      // Storage can be unavailable (private mode); the choice then lasts for this page view.
    }

    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { view } }));
  }

  /** Mirrors the active view on every toggle instance. */
  #sync = () => {
    const active = document.documentElement.dataset.taxDisplay ?? 'net';

    for (const option of this.refs.options ?? []) {
      option.setAttribute('aria-pressed', String(option.dataset.value === active));
    }
  };
}

if (!customElements.get('tax-display-component')) {
  customElements.define('tax-display-component', TaxDisplayComponent);
}
